import { html } from 'lit-element';

import { LightLitElement } from '@elab/components';
import connect from '@elab/core/store/connect';
import { actions as globalActions } from '@elab/core/store/global';
import { _getDataField } from '@elab/core/utils/dynaForms/selectors';
import { getActions } from '@elab/core/utils/dynaForms';
import { domLoaded } from '@elab/core/utils/domLoaded';
import { loadAsset } from '@elab/core/utils/helpers';
import { COMPONENT_CONNECT, COMPONENT_DISCONNECT } from '@elab/core/utils/dynaReduxHelpers';

import { actions } from './redux';

export class Form extends LightLitElement {
    static get properties() {
        return {
            loaded: { type: Boolean, reflect: true },
            storeName: { type: String, attribute: 'store-name' },
            formId: { type: String, attribute: 'form-id' },
            name: { type: String },
            successRedirectLink: {
                type: String,
                attribute: 'success-redirect-link',
            },
            failRedirectLink: { type: String, attribute: 'fail-redirect-link' },
            cssClass: { type: String, attribute: 'css-class' },
            otherErrorText: { type: String, attribute: 'other-error-text' },
            validationErrorText: { type: String, attribute: 'validation-error-text' },
            hostDialog: { type: String, attribute: 'host-dialog' },
            addShippingDestination: { type: Boolean, attribute: 'add-shipping-destination' },
            appId: { type: String, attribute: 'app-id' },
        };
    }

    constructor() {
        super();
        this.cssClass = '';
        this.storeName = '';
        this.otherErrorText = '';
        this.validationErrorText = '';
        this.successRedirectLink = '';
        this.failRedirectLink = '';
        this.addShippingDestination = false;
        this.appId = '';
        loadAsset('XRegExp');
    }

    afterConnect() {
        this.storeName = this.storeName || this.formId;
        this.dispatchEvent(new CustomEvent(COMPONENT_CONNECT));
        this.dispatchEvent(new CustomEvent('FORM-INITIALIZE'));
    }

    beforeDisconnect() {
        this.dispatchEvent(new CustomEvent(COMPONENT_DISCONNECT));
    }

    firstUpdated() {
        if (!this.loaded) {
            this.loaded = true;
        }
    }

    handleFocus() {
        this.dispatchEvent(new CustomEvent('SET_FOCUSED'));
    }

    handleBlur() {
        this.dispatchEvent(new CustomEvent('SET_UNFOCUSED'));
    }

    submit(evt) {
        if (!this.isErrorTextDefined && (this.otherErrorText || this.validationErrorText)) {
            this.isErrorTextDefined = true;
            this.dispatchEvent(new CustomEvent('SET_ERROR_TEXT'));
        }

        evt.preventDefault();
        this.dispatchEvent(new CustomEvent('FORM-SUBMIT'));
        return false;
    }

    get formClassName() {
        return `e2-form ${this.cssClass}`;
    }

    render() {
        return html`
            <form
                class="${this.formClassName}"
                @focus=${this.handleFocus}
                @blur=${this.handleBlur}
                @submit=${this.submit}
            >
                <v-slot name="content"></v-slot>
            </form>
        `;
    }
}

Form.prototype.handleFocus.capture = true;
Form.prototype.handleBlur.capture = true;

export const mapFormDispatchToEvents = (dispatch, ctx) => ({
    'FORM-INITIALIZE': () => {
        dispatch(
            globalActions.doFormInitialize(
                ctx.storeName,
                actions.initialize(
                    ctx.storeName,
                    ctx.successRedirectLink,
                    ctx.failRedirectLink,
                    ctx.hostDialog,
                    ctx.formId,
                    ctx,
                ),
            ),
        );
    },
    SET_FOCUSED: () => dispatch(actions.setFocused(ctx.storeName)),
    SET_UNFOCUSED: () => dispatch(actions.setUnfocused(ctx.storeName)),
    SET_ERROR_TEXT: () =>
        dispatch(
            getActions(ctx.storeName).setErrorsText({
                other: ctx.otherErrorText || '',
                validation: ctx.validationErrorText || '',
            }),
        ),
    'FORM-SUBMIT': e => dispatch(actions.submit(ctx.storeName, e.detail)),
    'CHANGE-VALUE': e => dispatch(actions.setDatafieldValue(ctx.storeName, ctx.name, e.detail)),
    'HAS-SINGLE-ERROR-MODE': ({ detail }) =>
        dispatch(actions.setSingleErrorMode(ctx.storeName, ctx.name, detail)),
    'INITIALIZE-DATA-FIELD-VALUE': e =>
        dispatch(actions.initializeDatafieldValue(ctx.storeName, ctx.name, e.detail)),
    'REMOVE-DATA-FIELD-VALUE': () =>
        dispatch(actions.initializeDatafieldValue(ctx.storeName, ctx.name)),
    ADD_FIELD_ITEM: evt => {
        const { storeName, name } = evt.detail;
        dispatch(actions.addFieldItem(storeName, name));
    },
    REMOVE_FIELD_ITEM: evt => {
        const { storeName, name, index } = evt.detail;
        dispatch(actions.removeFieldItem(storeName, name, index));
    },
    'FOCUS-FIELD': evt => {
        const { fieldName } = evt.detail;
        dispatch(getActions(ctx.storeName).focusField(fieldName));
    },
    'BLUR-FIELD': evt => {
        const { fieldName } = evt.detail;
        dispatch(getActions(ctx.storeName).blurField(fieldName));
    },
    'TOUCH-FIELD': evt => {
        const { fieldName } = evt.detail;
        dispatch(getActions(ctx.storeName).touchField(fieldName));
    },
});

const onConnect = el => {
    let parentNode = el;
    let fieldPrefix = '';
    let fieldName = el.name || '';

    if (parentNode.storeName) return;

    do {
        parentNode = parentNode.parentNode;
        if (!parentNode || !parentNode.storeName) continue;

        if (!el.storeName) {
            el.storeName = parentNode.storeName;
        }

        if (parentNode.name && parentNode.name !== el.name) {
            if (el.name) {
                fieldPrefix = parentNode.name;
            } else {
                fieldName = parentNode.name;
            }
        }
    } while (parentNode && !parentNode.storeName);

    el.name = `${fieldPrefix ? `${fieldPrefix}.` : ''}${fieldName}`;
};

class _FormElement extends LightLitElement {
    static get properties() {
        return {
            storeName: { type: String, attribute: 'store-name' },
            name: { type: String },
            fieldValue: { type: Object /* {value: Any, error: String} */ },
        };
    }

    constructor() {
        super();

        this.listeners = [];
    }

    changeValue(value) {
        this.dispatchEvent(new CustomEvent('CHANGE-VALUE', { detail: value }));
    }

    hasSingleErrorMode() {
        const event = new CustomEvent('HAS-SINGLE-ERROR-MODE', {
            detail: this.singleErrorMode,
        });
        this.dispatchEvent(event);
    }

    submit(evt, dialogInfo = {}, noScroll) {
        if (evt) {
            evt.preventDefault();
        }
        this.dispatchEvent(
            new CustomEvent('FORM-SUBMIT', {
                detail: {
                    dialogInfo,
                    noScroll,
                },
            }),
        );
        return false;
    }

    getState(state) {
        return (this.storeName && state[this.storeName]) || {};
    }

    isErrorDefined() {
        return this.fieldValue && this.fieldValue.error;
    }

    connectedCallback() {
        if (super.connectedCallback) {
            super.connectedCallback();
        }

        onConnect(this);
        this.listeners = domLoaded(() => {
            if (this.storeName) return;
            onConnect(this);
        });
    }

    disconnectedCallback() {
        this.listeners.forEach(unsubscribe => unsubscribe());

        if (super.disconnectedCallback) {
            super.disconnectedCallback();
        }
    }

    getFieldValue(state) {
        return _getDataField(this.getState(state).data || {}, this.name);
    }
}

const mapFormElementStateToProps = (state, ctx) => {
    return ctx.name ? { fieldValue: ctx.getFieldValue(state) } : {};
};

export const FormElement = connect(
    mapFormElementStateToProps,
    mapFormDispatchToEvents,
)(_FormElement);
